<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import type { Readable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import { statusText, type GenOrder } from '../order'
    import Icon from '@iconify/svelte'

    import { gettext as _, resolve } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, groups as ugroups } from 'hydria/services/session'
    import type { SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, spath } from 'hydria/svelte_helpers'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const orderId = getContext('orderId') as Readable<string>
    const Order = getContext('Order') as GenOrder

    const status = sderived(Order.status, ($status) => $status)

    let order = sderived(Order.orderStore, ($order) => $order)

    let ownerName: string | null = $state(null)
    let ownerLoading = $state(false)
    let loadedOwnerId: string

    async function loadOwner(ownerId: string | null) {
        if (ownerId == loadedOwnerId) return
        if (!ownerId) {
            ownerName = null
            return
        }

        ownerLoading = true
        ownerName = null
        let r: SimpleFetchResult

        try {
            r = await simpleFetch('GET', `/json/wdom-user/1/${ownerId}/name`)
            ownerName = r.data.name ? r.data.name : ''
            if (r.data.org) ownerName = (ownerName ? ownerName : '–') + ` (${r.data.org})`

            loadedOwnerId = ownerId
        } catch (e) {
            console.error('Failed to load order owner name:', e)
        }

        ownerLoading = false
    }

    let preDeleteModal = $state(false)

    let deleting = $state(false)
    let deleted = $state(false)

    let deleteReason: string = $state('')
    let cleanDeleteReason = $derived(deleteReason ? deleteReason.trim() : '')

    async function deleteOrder() {
        let r: SimpleFetchResult

        deleting = true
        try {
            r = await simpleFetch('DELETE', `/json/ecom/1/order/${$orderId}` + (!canDelete && canForceDelete ? '/force' : ''), { reason: cleanDeleteReason })
            notify({
                type: 'success',
                title: 'Effacement d’une commande',
                text: 'Commande effacée.',
            })
            deleted = true
        } catch (e) {
            console.error('Failed to delete order:', e)
            notify({
                type: 'danger',
                title: 'Effacement d’une commande',
                text: 'La commande n’a pas pu être effacée.',
            })
        }

        deleting = false
    }

    let changeUserModal = $state(false)
    let changeUserSearchText: string | null = $state(null)
    let loadingUsers = $state(false)
    let changeUserInput: HTMLInputElement | undefined = $state()

    // we don't care about other properties
    interface IUser {
        id: string
        email: string
        ecom_comment?: string
        metadata: {
            name: string
        }
    }
    let userList: IUser[] = $state([])
    let selectedUser: IUser | null = $state(null)

    function openChangeUser() {
        changeUserInput = undefined
        changeUserModal = true
        changeUserSearchText = null
        selectedUser = null
    }

    async function loadUserSearch(text: string) {
        userList = []

        if (changeUserSearchText == null) return
        let sdata = {
            text: changeUserSearchText.trim(),
            skip: 0,
            limit: 50,
        }
        if (sdata.text == '') return

        let r: SimpleFetchResult

        loadingUsers = true
        try {
            r = await simpleFetch('GET', '/json/wdom-user/1/mini-lookup', { data: JSON.stringify(sdata) })
            userList = r.data.items
        } catch (e: any) {
            console.error('Failed to load user list:', e)
            notify({
                type: 'danger',
                title: 'Chargement des utilisateurs',
                text: 'Une erreur est survenue lors de la recherche.',
            })
        }

        loadingUsers = false
    }

    let settingNewUser = $state(false)
    async function setNewUser(user: IUser | null) {
        if (!user) return
        settingNewUser = true

        let r: SimpleFetchResult

        try {
            r = await simpleFetch('POST', `/json/ecom/1/change-owner/${$orderId}`, { user_id: user.id })
            Order.reset($orderId)
            notify({
                type: 'success',
                title: 'Changement de client',
                text: 'Cette commande appartient maintenant à un nouveau client.',
            })
        } catch (e) {
            console.error('Failed to set user:', e)
            notify({
                type: 'danger',
                title: 'Changement de client',
                text: 'Une erreur est survenue lors du changement du client associé à cette commande.',
            })
        }

        settingNewUser = false
        changeUserModal = false
    }

    onDestroy(() => {
        if ((preDeleteModal || changeUserModal) && document.documentElement.classList.contains('modal-open')) document.documentElement.classList.remove('modal-open')
    })

    let loadedOwner: string | undefined
    $effect(() => {
        if ($status == 'ready' && $order && (loadedOwner == null || loadedOwner != $order.owner)) {
            loadedOwner = $order.owner
            loadOwner($order.owner)
        }
    })

    // ##################################################### DELETE

    let canDelete = $derived($status == 'ready' && $order && ['draft', 'ready', 'quotation'].includes($order.status))
    let canForceDelete = $derived($status == 'ready' && $order && (['draft', 'ready', 'quotation'].includes($order.status) || ($ugroups.has('wdom-cart-force-delete') && $order.status != 'deleted')))
    $effect(() => {
        if ($status != 'ready' && preDeleteModal) preDeleteModal = false
    })
    $effect(() => {
        if (changeUserModal && changeUserInput) {
            changeUserInput.focus()
        }
    })
    $effect(() => {
        if ((preDeleteModal || changeUserModal) && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!preDeleteModal && !changeUserModal && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<style>
    .order-status {
        display: flex;
        gap: 2em;
        justify-content: space-between;
        align-items: center;
    }
    .os-item {
        flex: 0 0 auto;
        display: flex;
        gap: 0.5em;
        align-items: center;
    }
    .os-item > * {
        flex: 0 0 auto;
    }
    .os-label {
        font-weight: 500;
    }

    .not-done {
        color: var(--bs-gray-500);
        text-decoration: line-through;
    }
    .os-important {
        color: var(--bs-danger);
    }

    .os-item.muted * {
        color: var(--bs-gray-500);
    }

    .user-cloud {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    .order-deleted-reason {
        text-align: center;
        font-style: italic;
    }
</style>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape' && (preDeleteModal || changeUserModal) && !deleting && !deleted) {
            preDeleteModal = false
            changeUserModal = false
        }
    }}
/>

<div class="card">
    <div class="card-body">
        <div class="order-status">
            <div class="os-item os-owner">
                <div class="os-label">{$_('Client:')}</div>
                <div class="os-value">
                    {#if $status == 'loading' || ownerLoading}
                        <LoadingPattern style="width: 12em;" pattern={['x']} />
                    {:else if ownerName}
                        <a use:sanchor href={spath(`/wdom-user/${$order.owner}`)}>{ownerName}</a>
                    {:else}
                        <a use:sanchor href={spath(`/wdom-user/${$order.owner}`)} class="text-muted">{$_('Unknown user')}</a>
                    {/if}
                </div>
                {#if $status != 'loading' && !ownerLoading && ownerName}
                    <div class="os-value"><button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => openChangeUser()}><Icon inline icon="mdi:account-switch" /></button></div>
                {/if}
            </div>

            <div class="os-item os-comment" class:muted={$status == 'loading' || !$order.meta || !$order.meta.accounting_comment}>
                <div class="os-label">{$_('Accounting:')}</div>
                <div class="os-value">
                    {#if $status == 'loading'}
                        <LoadingPattern style="width: 12em;" pattern={['x']} />
                    {:else if $order.meta && $order.meta.accounting_comment}
                        {$order.meta.accounting_comment}
                    {/if}
                </div>
            </div>

            <div class="os-item os-status">
                <div class="os-label">{$_('Status:')}</div>
                <div class="os-value">
                    {#if $status == 'loading'}
                        <LoadingPattern style="width: 12em;" pattern={['x']} />
                    {:else}
                        <span class:text-danger={$order.status == 'deleted'}>{$resolve(statusText($order.status))}</span>
                        (<span class:text-success={$order.finalized} class:not-done={!$order.finalized}>{$_('finalized')}</span>,
                        <span class:text-success={$order.paid} class:not-done={!$order.paid}>{$_('paid')}</span>)
                    {/if}
                </div>
            </div>

            {#if $status != 'loading' && $order.taxes_exempted}
                <div class="os-item os-exempt">
                    <div class="os-important">
                        {$_('Exemption de taxes, « {reason} »', { reason: $order.tax_exemption_reason })}
                    </div>
                </div>
            {/if}

            <div class="os-item os-delete">
                <div class="os-value">
                    <button type="button" class="btn btn-sm" class:btn-outline-secondary={!canDelete && !canForceDelete} disabled={!canDelete && !canForceDelete} class:btn-warning={canDelete || canForceDelete} onclick={() => (preDeleteModal = true)}>
                        <Icon inline icon="fa:trash" />
                    </button>
                </div>
            </div>
        </div>
    </div>
    {#if $order.deleted_reason}
        <div class="card-footer text-danger">
            <div class="order-deleted-reason">
                {$order.deleted_reason}
            </div>
        </div>
    {/if}
</div>

{#if preDeleteModal}
    <div
        class="modal modal-ds-records"
        role="dialog"
        transition:fade={{ duration: 250 }}
        aria-hidden="true"
        onclick={() => {
            if (!deleting && !deleted) preDeleteModal = false
        }}
    >
        <div
            class="modal-dialog modal-lg"
            onclick={(e) => {
                e.stopPropagation()
            }}
            aria-hidden="true"
        >
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>{$_('Delete this order')}</div>
                </div>

                {#if deleted}
                    <div class="modal-body">
                        <p>La commande a été effacée.</p>
                    </div>

                    <div class="modal-footer">
                        <a use:sanchor href={spath(`/wdom-user/${$order.owner}`)} class="btn btn-outline-secondary">{$_('Compte client')}</a>
                        <a use:sanchor href={spath('/ecom/orders')} class="btn btn-outline-secondary">{$_('Liste des commandes')}</a>
                    </div>
                {:else}
                    <div class="modal-body">
                        {#if !canDelete}
                            <p><span class="text-danger">Cet achat ne peut normalement pas être effacé.</span></p>
                        {:else}
                            <p>Êtes-vous certain de vouloir détruire cette commande ?</p>
                        {/if}
                        <textarea class="form-control" bind:value={deleteReason}></textarea>
                    </div>

                    <div class="modal-footer">
                        <button class="btn btn-danger" type="button" disabled={deleting} onclick={() => deleteOrder()}>
                            {#if deleting}
                                <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                            {/if}
                            {#if !canDelete}
                                {$_('Force delete')}
                            {:else}
                                {$_('Delete')}
                            {/if}
                        </button>
                        <button class="btn btn-outline-secondary" type="button" disabled={deleting} onclick={() => (preDeleteModal = false)}>{$_('Cancel')}</button>
                    </div>
                {/if}
            </div>
        </div>
        <div class="modal-backdrop"></div>
    </div>
{/if}

{#if changeUserModal}
    <div
        class="modal modal-ds-records"
        role="dialog"
        transition:fade={{ duration: 250 }}
        aria-hidden="true"
        onclick={() => {
            if (!settingNewUser && !loadingUsers) changeUserModal = false
        }}
    >
        <div
            class="modal-dialog modal-lg"
            onclick={(e) => {
                e.stopPropagation()
            }}
            aria-hidden="true"
        >
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>{$_('Déplacer vers un nouveau client')}</div>
                </div>

                <div class="modal-body">
                    <form
                        onsubmit={(e) => {
                            e.preventDefault()
                            loadUserSearch(changeUserSearchText ? changeUserSearchText : '')
                        }}
                    >
                        <div class="input-group">
                            <input type="text" class="form-control" bind:this={changeUserInput} bind:value={changeUserSearchText} />
                            <button type="submit" class="btn btn-sm btn-primary" disabled={loadingUsers || changeUserSearchText == null || changeUserSearchText.trim() == ''}>Lookup</button>
                        </div>
                    </form>
                </div>

                {#if userList && userList.length}
                    <div class="modal-body">
                        <span class="user-cloud">
                            {#each userList as u}
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary"
                                    onclick={() => {
                                        selectedUser = u
                                    }}
                                    class:active={selectedUser == u}
                                    >{u.metadata.name}{#if u.ecom_comment}&nbsp;(<strong>{u.ecom_comment}</strong>){/if}</button
                                >
                            {/each}
                        </span>
                    </div>
                {:else}
                    <div class="modal-body">Aucun résultat</div>
                {/if}

                <div class="modal-footer">
                    <button class="btn" type="button" class:btn-danger={!settingNewUser && selectedUser} class:btn-outline-danger={settingNewUser || !selectedUser} disabled={settingNewUser || !selectedUser} onclick={() => setNewUser(selectedUser)}>
                        {#if settingNewUser}
                            <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        {/if}
                        {$_('Changer l’utilisateur')}
                    </button>
                    <button class="btn btn-outline-secondary" type="button" disabled={settingNewUser || loadingUsers} onclick={() => (changeUserModal = false)}>{$_('Cancel')}</button>
                </div>
            </div>
        </div>
        <div class="modal-backdrop"></div>
    </div>
{/if}
